import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LinkIcon,
  CheckIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchMspWebhookConfigs,
  updateMspWebhookConfig,
  removeMspWebhookConfig,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy HH:mm'); } catch { return dateStr; }
}

export default function WebhookConfigManager() {
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isGlobalAdmin;
  const isMspAdmin = !!user?.isMspAdmin;
  const adminMspCodes = useMemo(
    () => (user?.adminMspCodes || []).map((c) => `${c}`.toUpperCase()),
    [user]
  );
  const canEdit = useCallback(
    (mspCode) => isGlobalAdmin || adminMspCodes.includes(`${mspCode || ''}`.toUpperCase()),
    [isGlobalAdmin, adminMspCodes]
  );
  const anyAdmin = isGlobalAdmin || isMspAdmin;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [edits, setEdits] = useState({}); // code -> { url, enabled }
  const [savingCode, setSavingCode] = useState(null);
  const [removingCode, setRemovingCode] = useState(null);
  const [rowError, setRowError] = useState({});

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMspWebhookConfigs();
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      const seed = {};
      list.forEach((r) => { seed[r.mspCode] = { url: r.webhookUrl || '', enabled: !!r.enabled }; });
      setEdits(seed);
    } catch (err) {
      setError(err.message || 'Failed to load webhook config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setEdit = (code, patch) => setEdits((prev) => ({ ...prev, [code]: { ...prev[code], ...patch } }));

  const isDirty = useCallback((r) => {
    const e = edits[r.mspCode];
    if (!e) return false;
    return e.url !== (r.webhookUrl || '') || e.enabled !== !!r.enabled;
  }, [edits]);

  const handleSave = async (r) => {
    const e = edits[r.mspCode];
    if (!e) return;
    setRowError((prev) => ({ ...prev, [r.mspCode]: null }));
    setSavingCode(r.mspCode);
    try {
      const updated = await updateMspWebhookConfig(r.mspCode, { webhookUrl: e.url, enabled: e.enabled });
      setRows((prev) => prev.map((item) => (item.mspCode === r.mspCode ? { ...item, ...updated } : item)));
      setEdit(r.mspCode, { url: updated.webhookUrl || '', enabled: !!updated.enabled });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.mspCode]: err.message || 'Failed to save' }));
    } finally {
      setSavingCode(null);
    }
  };

  const handleRemove = async (r) => {
    if (!window.confirm(`Remove the webhook for ${r.mspCode}? Alerts for this MSP will stop posting to the webhook (email/Teams unaffected).`)) return;
    setRowError((prev) => ({ ...prev, [r.mspCode]: null }));
    setRemovingCode(r.mspCode);
    try {
      await removeMspWebhookConfig(r.mspCode);
      setRows((prev) => prev.map((item) => (item.mspCode === r.mspCode ? { ...item, webhookUrl: '', enabled: false } : item)));
      setEdit(r.mspCode, { url: '', enabled: false });
    } catch (err) {
      setRowError((prev) => ({ ...prev, [r.mspCode]: err.message || 'Failed to remove' }));
    } finally {
      setRemovingCode(null);
    }
  };

  const enabledCount = useMemo(() => rows.filter((r) => !!r.enabled).length, [rows]);

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <LinkIcon className="h-7 w-7 text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-100">MSP Webhook Routing</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Per-MSP webhook destination for detection alerts. When enabled, the dispatcher POSTs alert
          payloads to this MSP's webhook URL. This channel is independent of the Teams channel and email
          routing. URLs must be <span className="font-mono">https://</span>.
        </p>
      </div>

      {!anyAdmin && (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to change webhook routing.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={load} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      <div className="flex items-center justify-end mb-3">
        <span className="text-xs text-gray-500">
          {rows.length} MSP{rows.length === 1 ? '' : 's'} · {enabledCount} with webhook enabled
        </span>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="text-sm w-full">
            <thead>
              <tr className="border-b border-gray-800">
                {['MSP', 'Webhook URL (https)', 'Enabled', 'Last Updated', ''].map((label) => (
                  <th key={label} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr><td colSpan={5} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <ArrowPathIcon className="h-8 w-8 text-blue-500 animate-spin" />
                    <span className="text-gray-500 text-sm">Loading webhook config...</span>
                  </div>
                </td></tr>
              )}

              {!loading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-12 text-center">
                  <LinkIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                  <p className="text-gray-400 font-medium">No MSPs configured</p>
                  <p className="text-gray-600 text-sm mt-1">Set the MSP_CATALOG app setting to populate this list.</p>
                </td></tr>
              )}

              {!loading && rows.map((r) => {
                const e = edits[r.mspCode] || { url: '', enabled: false };
                const editable = canEdit(r.mspCode);
                const dirty = isDirty(r);
                const saving = savingCode === r.mspCode;
                const removing = removingCode === r.mspCode;
                return (
                  <tr key={r.mspCode} className="hover:bg-gray-800/50 transition-colors align-top">
                    <td className="px-4 py-3">
                      <div className="font-mono text-gray-200">{r.mspCode}</div>
                      {r.displayName && <div className="text-xs text-gray-500">{r.displayName}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={e.url}
                        disabled={!editable}
                        onChange={(ev) => setEdit(r.mspCode, { url: ev.target.value })}
                        placeholder="https://hooks.example.com/services/..."
                        className="input-field w-full text-xs disabled:opacity-60"
                      />
                      {rowError[r.mspCode] && <p className="mt-1 text-xs text-red-400">{rowError[r.mspCode]}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={e.enabled}
                          disabled={!editable}
                          onChange={(ev) => setEdit(r.mspCode, { enabled: ev.target.checked })}
                          className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500 disabled:opacity-40"
                        />
                        <span className={`text-xs ${e.enabled ? 'text-green-400' : 'text-gray-500'}`}>{e.enabled ? 'Enabled' : 'Disabled'}</span>
                      </label>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(r.updatedUtc)}
                      {r.updatedBy && <div className="text-gray-600 truncate max-w-[180px]" title={r.updatedBy}>{r.updatedBy}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {editable && (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => handleSave(r)}
                            disabled={!dirty || saving || removing}
                            className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                          >
                            {saving ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <CheckIcon className="h-3.5 w-3.5" />}
                            Save
                          </button>
                          <button
                            onClick={() => handleRemove(r)}
                            disabled={removing || saving || (!r.webhookUrl)}
                            title={r.webhookUrl ? 'Remove webhook' : 'Nothing to remove'}
                            className="btn-secondary px-2 py-1.5 text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                          >
                            {removing ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <TrashIcon className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
